import railyApiService from "../raily-api.service";
import driverAddRequestFactory from "./add/driver-add-request.factory";
import DriverAddRequest, {
  DriverAddRequestBody,
} from "./add/driver-add.request";
import DriverAddResponse from "./add/driver-add.response";
import DriverDetailsRequest from "./details/driver-details.request";
import DriverDetailsResponse from "./details/driver-details.response";
import DriverContractsListRequest from "./contracts/list/driver-contracts-list.request";
import DriverContractsListResponse from "./contracts/list/driver-contracts-list.response";
import DriverContractsDriversListRequest from "./contracts/drivers-list/driver-contracts-drivers-list.request";
import DriverContractsDriversListResponse from "./contracts/drivers-list/driver-contracts-drivers-list.response";
import DriverTaxiDriverAssociationsRequest from "./taxi-driver/associations/driver-taxi-driver-associations.request";
import DriverTaxiDriverAssociationsResponse from "./taxi-driver/associations/driver-taxi-driver-associations.response";
import DriverPlanListRequest from "./plan/list/driver-plan-list.request";
import DriverPlanListResponse from "./plan/list/driver-plan-list.response";
import DriverCandidatureActiveRequest from "./candidature/active/driver-candidature-active.request";
import DriverCandidatureActiveResponse from "./candidature/active/driver-candidature-active.response";
import DriverPotentialPlanRequest from "./potential-plan/driver-potential-plan.request";
import DriverPotentialPlanResponse from "./potential-plan/driver-potential-plan.response";
import DriverPlanAcceptRequest from "./plan/accept/driver-plan-accept.request";
import DriverPlanAcceptResponse from "./plan/accept/driver-plan-accept.response";
import DriverCandidatureDeleteRequest from "./candidature/delete/driver-candidature-delete.request";
import DriverCandidatureDeleteResponse from "./candidature/delete/driver-candidature-delete.response";
import driverPlanEntryCheckoutEventAddRequestFactory from "./plan-entry/checkout-event/add/driver-plan-entry-checkout-event-add-request.factory";
import DriverPlanEntryCheckoutEventAddRequest, {
  DriverPlanEntryCheckoutEventAddRequestBody,
} from "./plan-entry/checkout-event/add/driver-plan-entry-checkout-event-add.request";
import DriverPlanEntryCheckoutEventAddResponse from "./plan-entry/checkout-event/add/driver-plan-entry-checkout-event-add.response";
import DriverPlanEntryCheckoutEventDeleteRequest from "./plan-entry/checkout-event/delete/driver-plan-entry-checkout-event-delete.request";
import DriverPlanEntryCheckoutEventDeleteResponse from "./plan-entry/checkout-event/delete/driver-plan-entry-checkout-event-delete.response";
import driverPlanEntryCheckoutEventUpdateRequestFactory from "./plan-entry/checkout-event/update/driver-plan-entry-checkout-event-update-request.factory";
import DriverPlanEntryCheckoutEventUpdateRequest, {
  DriverPlanEntryCheckoutEventUpdateRequestBody,
} from "./plan-entry/checkout-event/update/driver-plan-entry-checkout-event-update.request";
import DriverPlanEntryCheckoutEventUpdateResponse from "./plan-entry/checkout-event/update/driver-plan-entry-checkout-event-update.response";

const driverApiService = () => {
  const getDetails = async (
    request: DriverDetailsRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverDetailsResponse> => {
    const url = `drivers/${request.driverId}`;

    return railyApiService().get<DriverDetailsResponse>({
      url,
      abortSignal,
    });
  };

  const addDriver = async (
    request: DriverAddRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverAddResponse> => {
    const url = `/drivers`;

    const body: DriverAddRequestBody =
      driverAddRequestFactory.createBody(request);

    return railyApiService().post({
      url,
      abortSignal,
      body,
    });
  };

  const getContractsList = (
    request: DriverContractsListRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverContractsListResponse> => {
    const url = `/drivers/${request.driverUuid}/contracts/taxi-driver`;

    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const getContractsDriversList = (
    request: DriverContractsDriversListRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverContractsDriversListResponse> => {
    const url = `/drivers/${request.driverUuid}`;
    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const getTaxiDriverAssociations = (
    request: DriverTaxiDriverAssociationsRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverTaxiDriverAssociationsResponse> => {
    const url = `/drivers/${request.driverUuid}/taxi-driver-associations`;
    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const getPlanList = (
    request: DriverPlanListRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverPlanListResponse> => {
    const url = `/drivers/${request.driverUuid}/plan`;

    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const getActiveCandidature = (
    request: DriverCandidatureActiveRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverCandidatureActiveResponse> => {
    const url = `/drivers/${request.driverUuid}/candidature`;

    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const getDriverRoutePlansAfterCandidatureApproval = (
    request: DriverPotentialPlanRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverPotentialPlanResponse> => {
    const url = `/drivers/${request.driverUuid}/potential-plan`;

    return railyApiService().get({
      url,
      abortSignal,
    });
  };

  const acceptPlanCandidature = (
    request: DriverPlanAcceptRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverPlanAcceptResponse> => {
    const url = `/drivers/${request.driverUuid}/plan`;

    return railyApiService().put({
      url,
      abortSignal,
    });
  };

  const deletePlanCandidature = (
    request: DriverCandidatureDeleteRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverCandidatureDeleteResponse> => {
    const url = `/drivers/${request.driverUuid}/candidature`;

    return railyApiService().delete({
      url,
      abortSignal,
    });
  };

  const updateCheckoutEvent = (
    request: DriverPlanEntryCheckoutEventUpdateRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverPlanEntryCheckoutEventUpdateResponse> => {
    const url = `/drivers/${request.driverUuid}/plan-entries/${request.planEntryUuid}/checkout-events/${request.checkoutEventUuid}`;

    const body: DriverPlanEntryCheckoutEventUpdateRequestBody =
      driverPlanEntryCheckoutEventUpdateRequestFactory.createBody(request);

    return railyApiService().put({
      url,
      abortSignal,
      body,
    });
  };

  const deleteCheckoutEvent = (
    request: DriverPlanEntryCheckoutEventDeleteRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverPlanEntryCheckoutEventDeleteResponse> => {
    const url = `/drivers/${request.driverUuid}/plan-entries/${request.planEntryUuid}/checkout-events/${request.checkoutEventUuid}`;

    return railyApiService().delete({
      url,
      abortSignal,
    });
  };

  const addCheckoutEvent = (
    request: DriverPlanEntryCheckoutEventAddRequest,
    abortSignal: AbortSignal,
  ): Promise<DriverPlanEntryCheckoutEventAddResponse> => {
    const url = `/drivers/${request.driverUuid}/plan-entries/${request.planEntryUuid}/checkout-events`;

    const body: DriverPlanEntryCheckoutEventAddRequestBody =
      driverPlanEntryCheckoutEventAddRequestFactory.createBody(request);

    return railyApiService().post({
      url,
      abortSignal,
      body,
    });
  };

  return {
    getDetails,
    addDriver,
    getContractsList,
    getContractsDriversList,
    getTaxiDriverAssociations,
    getPlanList,
    getActiveCandidature,
    getDriverRoutePlansAfterCandidatureApproval,
    acceptPlanCandidature,
    deletePlanCandidature,
    updateCheckoutEvent,
    deleteCheckoutEvent,
    addCheckoutEvent,
  };
};

export default driverApiService;
